import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { linkRentalPortalApplicantDocument } from '../../../../../../../api'
import { useNotification } from '../../../../../../../hooks'
import {
  FILE_UPLOAD_DROPZONE_OPTIONS,
  useFileDropzone,
} from '../../../../../../File'
import { toApplicantDocumentLinkData } from '../../../../../../RentalPortalApplicant'
import { useApplicantProfileContext } from '../../../../RentalPortalApplicantProfile.hooks'
import { APPLICANT_CHECKLIST_ATTACH_UPLOAD_OPTIONS } from '../../ApplicantDocumentChecklist.constants'

export function useApplicantDocumentAttach() {
  const { t } = useTranslation()

  const { handleAddErrorNotification } = useNotification()

  const {
    applicantData,
    applicantId,
    handleCloseModal,
    handleEntityReload,
    setModal,
  } = useApplicantProfileContext()

  const attachRef = useRef(null)

  const attachingKeyRef = useRef(null)

  const [attachingKey, setAttachingKey] = useState(null)

  function setAttaching(key) {
    attachingKeyRef.current = key

    setAttachingKey(key)
  }

  async function linkDocument({ documentKey, fileId, key }) {
    setAttaching(key)

    try {
      await linkRentalPortalApplicantDocument({
        data: toApplicantDocumentLinkData({ documentKey, fileId }),
        id: applicantId,
        key,
      })

      await handleEntityReload()
    } catch {
      handleAddErrorNotification({
        textButtonValue: t('RentalPortal.ListingRetryBtnKey'),
        title: t('RentalPortal.ApplicantDocumentAttachErrorKey'),
        onClickTextButton: () =>
          handleRetryLinkDocument({ documentKey, fileId, key }),
      })
    } finally {
      setAttaching(null)
    }
  }

  async function handleRetryLinkDocument(documentLink) {
    if (!attachingKeyRef.current) {
      await linkDocument(documentLink)
    }
  }

  function handleStartUpload() {
    setAttaching(attachRef.current?.key ?? null)
  }

  function handleFinishUpload(acceptedFiles) {
    const fileId = acceptedFiles[0]?.id

    return fileId
      ? linkDocument({ ...attachRef.current, fileId })
      : setAttaching(null)
  }

  function handleUploadErrors() {
    setAttaching(null)

    handleAddErrorNotification({
      title: t('RentalPortal.ApplicantDocumentAttachErrorKey'),
    })
  }

  const { getInputProps, open } = useFileDropzone({
    options: {
      ...FILE_UPLOAD_DROPZONE_OPTIONS,
      ...APPLICANT_CHECKLIST_ATTACH_UPLOAD_OPTIONS,
    },
    uploadParams: { parent: applicantData?.directory_id },
    onCloseModal: handleCloseModal,
    onFinishUpload: handleFinishUpload,
    onSetModal: setModal,
    onStartUpload: handleStartUpload,
    onUploadErrors: handleUploadErrors,
  })

  function handleAttachDocument({ documentKey, row }) {
    attachRef.current = { documentKey, key: row.key }

    open()
  }

  return {
    attachingKey,
    getAttachInputProps: getInputProps,
    handleAttachDocument,
  }
}
